import { DOMAttributes, ReactNode, useMemo } from 'react'
import clsx from 'clsx'
import makeStyles from '@mui/styles/makeStyles';
import ArrowRightIcon from '@mui/icons-material/ArrowRight'

import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import MPopover, { PopoverProps } from '@mui/material/Popover'

import Checkbox from './Checkbox'
import Reorderable, { ReorderableHandle } from '../Reorderable'

import {
  usePopupState,
  bindHover,
  bindPopover,
} from 'material-ui-popup-state/hooks'
import { CheckboxProps } from '@mui/material/Checkbox';

const useStyles = makeStyles(_theme => ({
  root: {
    padding: '10px 20px',
    userSelect: 'none',
  },
  menuOption: {
    display: 'flex',
    flexDirection: 'row',
    fontSize: 18,
    fontWeight: 600,
    borderBottom: '1px solid #e6e6e6',
    padding: 10,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  menuOptionClickable: {
    cursor: 'pointer',
  },
  grabber: {
    width: 30,
  },
  checkbox: {
    width: 30,
  },
  submenuCaret: {
    position: 'absolute',
    right: 10,
  },
}))

export type RenderSubmenuProps<T> = CheckboxMenuProps<T> & {
  parentMenuOption: T
}

export type MenuOptionProps<T> = {
  menuOption: T
  isReorderable?: (menuOption: T) => boolean
  isChecked: (menuOption: T) => boolean
  onChange: (menuOption: T) => CheckboxProps['onChange'] & DOMAttributes<HTMLDivElement>['onClick']
  render: (menuOption: T) => ReactNode
  renderSubmenu?: React.FC<RenderSubmenuProps<T>>
  entireOptionClickable?: boolean
}

function MenuOption<T>({ menuOption, isReorderable, isChecked, onChange, render, renderSubmenu, entireOptionClickable }: MenuOptionProps<T>) {
  const classes = useStyles()
  const handleChange = useMemo(() => onChange(menuOption), [menuOption, onChange])
  const popupState = usePopupState({ variant: 'popover', popupId: 'checkboxSubmenu' })

  return (
    <div className={clsx({[classes.menuOption]: true, [classes.menuOptionClickable]: entireOptionClickable})} role="menuitem" {...(renderSubmenu && bindHover(popupState))} onClick={entireOptionClickable && handleChange || undefined}>
      {isReorderable && (
        <div className={classes.grabber}>
          {isReorderable(menuOption) && (
            <ReorderableHandle/>
          )}
        </div>
      )}
      <div className={classes.checkbox}>
        <Checkbox
          style={{padding: 0}}
          checked={isChecked(menuOption)}
          onChange={handleChange}
        />
      </div>
      {render(menuOption)}
      {renderSubmenu && (
        <>
          <div className={classes.submenuCaret}>
            <ArrowRightIcon/>
          </div>
          {renderSubmenu({...bindPopover(popupState), parentMenuOption: menuOption, anchorOrigin: { horizontal: 'right', vertical: 'top' }, transformOrigin: { horizontal: 'left', vertical: 'top' }})}
        </>
      )}
    </div>
  )
}

export type CheckboxMenuProps<T> = Omit<PopoverProps, 'onChange'> & {
  menuOptions?: T[]
  reorderable?: boolean
  updatePosition?: unknown
  isChecked?: (menuOption: T) => boolean
  isReorderable?: (menuOption: T) => boolean
  onChange?: (menuOption: T) => CheckboxProps['onChange'] & DOMAttributes<HTMLDivElement>['onClick']
  render?: (menuOption: T) => ReactNode
  renderSubmenu?: React.FC<RenderSubmenuProps<T>>
  Popover?: React.ComponentType<PopoverProps>
}

export default function CheckboxMenu<MenuOptionType>({
  menuOptions = [],
  reorderable = false,
  updatePosition = () => {},
  isChecked = () => false,
  isReorderable,
  onChange = () => undefined,
  render = menuOption => menuOption as string,
  renderSubmenu,
  Popover = MPopover,
  ...popoverProps
}: CheckboxMenuProps<MenuOptionType>) {
  if(!reorderable) {
    isReorderable = undefined
  }

  const classes = useStyles()

  return (
    <Popover
      role="menu"
      {...popoverProps}
    >
      <div className={classes.root}>
        {reorderable && (
          <DndProvider backend={HTML5Backend}>
            {menuOptions.map((menuOption, index) => (
              <Reorderable key={index} object={menuOption} type="MenuOption" updatePosition={updatePosition}>
                <MenuOption {...{menuOption, isReorderable, isChecked, onChange, render, renderSubmenu}}/>
              </Reorderable>
            ))}
          </DndProvider>
        ) || (
          menuOptions.map((menuOption, index) => (
            <MenuOption key={index} entireOptionClickable={false} {...{menuOption, isReorderable, isChecked, onChange, render, renderSubmenu}}/>
          ))
        )}
      </div>
    </Popover>
  )
}
